import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './PersonalizationPage.css';

function PersonalizationPage({ apiUrl }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, token, loading: authLoading } = useAuth();
  const [step, setStep] = useState(0); // Start at step 0 for profile selection
  const [elderlyUserId, setElderlyUserId] = useState('');
  const [familyUserId, setFamilyUserId] = useState('');
  const [relationship, setRelationship] = useState('');
  const [personaText, setPersonaText] = useState('');
  const [languagePref, setLanguagePref] = useState('auto');
  const [voiceFile, setVoiceFile] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [existingProfiles, setExistingProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null); // Profile being edited
  const [elderlyUserIds, setElderlyUserIds] = useState([]);
  const [loadingElderlyUsers, setLoadingElderlyUsers] = useState(false);
  const [showPersonaInput, setShowPersonaInput] = useState(false); // Toggle for manual persona text
  const [elderlyLinkEmail, setElderlyLinkEmail] = useState('');
  const [linkedElderly, setLinkedElderly] = useState(null);
  const [linkMessage, setLinkMessage] = useState(null);
  
  // Voice recording state
  const [inputMethod, setInputMethod] = useState('upload'); // 'upload' or 'record'
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaStream, setMediaStream] = useState(null);
  const recordingTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timerStartTimeRef = useRef(null);
  const lastDisplayedSecondRef = useRef(-1);

  // Redirect elderly users to their simple home interface
  useEffect(() => {
    if (!authLoading && user?.user_role === 'elderly') {
      navigate('/home', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Load all profiles when auth is ready and token is available
  useEffect(() => {
    if (!authLoading && token) {
      console.log('✅ Auth ready, loading profiles...');
      loadExistingProfiles();
    }
  }, [authLoading, token]);

  // Check if returning from survey completion
  useEffect(() => {
    const surveyCompleteProfileId = searchParams.get('survey_complete');
    if (surveyCompleteProfileId) {
      setSuccess('Survey completed! Now upload your voice to finish setup.');
      setProfileId(surveyCompleteProfileId);
      setStep(2); // Go to voice upload step
      // Clear the URL param
      navigate('/personalization', { replace: true });
    }
  }, [searchParams, navigate]);

  // Start survey interview
  const handleStartSurvey = (pId) => {
    navigate(`/survey/${pId}`);
  };

  const handleStartEnrichment = (pId) => {
    navigate(`/enrich/${pId}`);
  };

  // Load all profiles created by the current user
  const loadExistingProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const response = await axios.get(`${apiUrl}/personalization/profiles`);
      if (response.data.status === 'success') {
        setExistingProfiles(response.data.profiles || []);
        // Extract unique elderly_user_ids from profiles
        const uniqueIds = [...new Set(response.data.profiles.map(p => p.elderly_user_id).filter(Boolean))];
        setElderlyUserIds(uniqueIds);
      }
    } catch (err) {
      console.error('Failed to load profiles:', err);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleElderlyUserIdChange = (value) => {
    setElderlyUserId(value);
    localStorage.setItem('elderly_user_id', value);
    // Reload all profiles and filter client-side if needed
    loadExistingProfiles().then(() => {
      if (value) {
        // Filter client-side after reload
        const allProfiles = [...existingProfiles];
        const filtered = allProfiles.filter(p => p.elderly_user_id === value);
        setExistingProfiles(filtered);
      }
    });
  };

  const handleSelectProfile = (selectedProfileId) => {
    setProfileId(selectedProfileId);
    window.location.href = `/conversation-realtime/${selectedProfileId}`;
  };

  const handleEditProfile = async (profile, e) => {
    e.stopPropagation();
    setEditingProfile(profile);
    setProfileId(profile.id);
    setRelationship(profile.relationship || '');
    setPersonaText(profile.persona_text || '');
    setLanguagePref(profile.language_pref || 'auto');
    setFamilyUserId(profile.family_user_id || '');
    setElderlyUserId(profile.elderly_user_id || '');
    setElderlyLinkEmail('');
    setLinkMessage(null);
    setLinkedElderly(null);

    try {
      const response = await axios.get(`${apiUrl}/personalization/profiles/${profile.id}`);
      if (response.data?.linked_elderly) {
        setLinkedElderly(response.data.linked_elderly);
      }
    } catch (err) {
      console.error('Failed to load profile details:', err);
    }

    setStep(4);
  };

  const handleLinkElderly = async () => {
    if (!profileId || !elderlyLinkEmail.trim()) {
      setLinkMessage({ type: 'error', text: 'Enter the elderly user\'s email address.' });
      return;
    }

    setLoading(true);
    setLinkMessage(null);
    try {
      const response = await axios.post(`${apiUrl}/elderly/link-profile`, {
        profile_id: profileId,
        elderly_email: elderlyLinkEmail.trim(),
      });
      setLinkedElderly(response.data.linked_elderly);
      setLinkMessage({ type: 'success', text: response.data.message });
      setElderlyLinkEmail('');
    } catch (err) {
      setLinkMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to link elderly account',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!profileId) {
      setError('No profile selected for update');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.put(
        `${apiUrl}/personalization/profiles/${profileId}`,
        {
          relationship: relationship,
          persona_text: personaText,
          language_pref: languagePref
        }
      );
      
      if (response.data.status === 'success') {
        setSuccess('Profile updated successfully!');
        // Reload profiles
        await loadExistingProfiles();
        setStep(0);
        setEditingProfile(null);
        setProfileId(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async (profileIdToDelete, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.delete(`${apiUrl}/personalization/profiles/${profileIdToDelete}`);
      if (response.data.status === 'success') {
        setSuccess('Profile deleted successfully');
        // Reload profiles
        await loadExistingProfiles();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${apiUrl}/personalization/create-profile`, {
        elderly_user_id: elderlyUserId,
        family_user_id: familyUserId,
        relationship: relationship,
        persona_text: personaText || 'Personalization interview pending',
        language_pref: languagePref
      });
      
      if (response.data.status === 'success') {
        const newProfileId = response.data.profile_id;
        setProfileId(newProfileId);
        setSuccess('Profile created successfully! Starting interview...');
        // Redirect to survey interview
        setTimeout(() => {
          navigate(`/survey/${newProfileId}`);
        }, 500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      console.log('🎤 startRecording called');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microphone access granted');
      setMediaStream(stream);
      
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const audioChunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        console.log('🛑 Recording stopped, processing audio...');
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm;codecs=opus' });
        
        // Convert WebM blob to File object for upload
        const audioFile = new File([audioBlob], 'recorded-voice.webm', {
          type: 'audio/webm;codecs=opus',
          lastModified: Date.now()
        });
        
        setRecordedAudio(audioFile);
        setVoiceFile(audioFile);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      };
      
      recorder.start();
      console.log('✅ MediaRecorder started');
      mediaRecorderRef.current = recorder;
      
      // Reset and start timer - capture start time in closure
      const startTime = Date.now();
      lastDisplayedSecondRef.current = -1;
      console.log(`⏱️ Timer started at: ${startTime}`);
      
      // Set recording state - this will trigger a re-render
      setRecordingTime(0);
      setIsRecording(true);
      console.log('✅ isRecording set to true');
      
      // Use setTimeout to ensure state update completes before starting interval
      // This prevents the interval from being lost during re-render
      // Capture startTime in closure to avoid ref issues
      setTimeout(() => {
        console.log('⏱️ Setting up interval after state update...');
        console.log(`⏱️ Start time in closure: ${startTime}`);
        
        // Set ref inside setTimeout to ensure it's set before interval starts
        timerStartTimeRef.current = startTime;
        
        // Clear any existing interval first
        if (recordingTimerRef.current) {
          console.log('⚠️ Clearing existing interval');
          clearInterval(recordingTimerRef.current);
        }
        
        recordingTimerRef.current = setInterval(() => {
          // Use the captured startTime from closure as fallback
          const timerStart = timerStartTimeRef.current || startTime;
          if (!timerStart) {
            console.warn('⚠️ timerStartTimeRef.current is null and startTime is missing!');
            return;
          }
          const elapsedSeconds = Math.floor((Date.now() - timerStart) / 1000);
          
          // Only update state if we've moved to a new second (including 0 to 1)
          if (elapsedSeconds !== lastDisplayedSecondRef.current) {
            lastDisplayedSecondRef.current = elapsedSeconds;
            console.log(`⏱️ Timer update: ${elapsedSeconds}s (display: ${Math.floor(elapsedSeconds / 60)}:${(elapsedSeconds % 60).toString().padStart(2, '0')})`);
            // Force state update - React will re-render
            setRecordingTime(elapsedSeconds);
          }
        }, 100); // Check every 100ms for smooth updates
        console.log(`✅ Interval created: ${recordingTimerRef.current}`);
      }, 0); // Execute after current call stack
      
    } catch (err) {
      console.error('❌ Error starting recording:', err);
      setError('Failed to access microphone: ' + err.message);
    }
  };
  
  const stopRecording = () => {
    console.log('🛑 stopRecording called', { 
      hasMediaRecorder: !!mediaRecorderRef.current, 
      isRecording, 
      hasTimer: !!recordingTimerRef.current 
    });
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      // Clear timer first
      if (recordingTimerRef.current) {
        console.log('⏱️ Clearing interval');
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      // Reset timer start time
      timerStartTimeRef.current = null;
      lastDisplayedSecondRef.current = -1;
      
      // Update state
      setIsRecording(false);
      console.log('✅ Recording stopped');
    } else {
      console.warn('⚠️ stopRecording called but conditions not met', {
        mediaRecorderRef: !!mediaRecorderRef.current,
        isRecording
      });
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      timerStartTimeRef.current = null;
    };
  }, [mediaStream]);

  const handleUploadVoice = async () => {
    const fileToUpload = inputMethod === 'record' ? recordedAudio : voiceFile;
    
    if (!fileToUpload || !profileId) {
      setError(inputMethod === 'record' 
        ? 'Please record a voice sample first' 
        : 'Please select a voice file');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('family_profile_id', profileId);
      
      const response = await axios.post(
        `${apiUrl}/personalization/upload-voice`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data.status === 'success') {
        setSuccess('Voice uploaded and processed successfully!');
        // Reload profiles to update voice_ready status
        await loadExistingProfiles();
        // If editing, go back to profile list, otherwise go to success step
        if (editingProfile) {
          setStep(0);
          setEditingProfile(null);
          setProfileId(null);
        } else {
          setStep(3);
        }
        // Reset recording state
        setRecordedAudio(null);
        setRecordingTime(0);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload voice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="personalization-page">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>Family Voice Profiles</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Welcome, {user?.username}</span>
            <button
              onClick={() => {
                logout();
                window.location.href = '/login';
              }}
              className="button-secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              Logout
            </button>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {step === 0 && (
          <div className="step-content">
            <h2>Select or Create Profile</h2>
            
            <div className="input-group">
              <label>Elderly User ID</label>
              {elderlyUserIds.length > 0 ? (
                <select
                  value={elderlyUserId}
                  onChange={(e) => handleElderlyUserIdChange(e.target.value)}
                  style={{ fontSize: '18px', padding: '12px', width: '100%', borderRadius: '8px' }}
                >
                  <option value="">-- Select an existing user or type new --</option>
                  {elderlyUserIds.map((id) => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={elderlyUserId}
                  onChange={(e) => handleElderlyUserIdChange(e.target.value)}
                  placeholder="Enter elderly user ID"
                  style={{ fontSize: '18px', padding: '12px' }}
                />
              )}
              {loadingElderlyUsers && (
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '5px' }}>Loading user list...</p>
              )}
            </div>

            {loadingProfiles ? (
              <p style={{ marginTop: '20px', fontSize: '16px', color: 'var(--text-muted)' }}>Loading profiles...</p>
            ) : existingProfiles.length > 0 ? (
              <div style={{ marginTop: '30px' }}>
                <h3 style={{ fontSize: '20px', marginBottom: '20px' }}>Your Profiles:</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {existingProfiles.map((profile) => (
                        <div
                          key={profile.id}
                          style={{
                            border: '2px solid var(--sage-400)',
                            borderRadius: '12px',
                            padding: '20px',
                            cursor: profile.voice_ready ? 'pointer' : 'default',
                            backgroundColor: profile.voice_ready ? 'var(--mint-50)' : 'var(--sage-50)',
                            position: 'relative'
                          }}
                          onClick={() => profile.voice_ready && handleSelectProfile(profile.id)}
                        >
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            display: 'flex',
                            gap: '5px'
                          }}>
                            <button
                              onClick={(e) => handleEditProfile(profile, e)}
                              style={{
                                background: 'var(--sage-600)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                              disabled={loading}
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => handleDeleteProfile(profile.id, e)}
                              style={{
                                background: 'var(--forest-700)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                              disabled={loading}
                            >
                              Delete
                            </button>
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                            {profile.relationship}
                          </div>
                          <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                            Family Member ID: {profile.family_user_id}
                          </div>
                          <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                            {profile.persona_text?.substring(0, 100)}...
                          </div>
                          <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                            {profile.survey_completed ? (
                              <span style={{ color: 'var(--sage-600)' }}>✓ Interview Complete</span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>○ Interview Pending</span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px' }}>
                            {profile.voice_ready ? (
                              <span style={{ color: 'var(--sage-600)', fontWeight: 'bold' }}>✓ Voice Ready</span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>○ Voice Pending</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
                            {profile.voice_ready ? (
                              <button
                                className="button-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectProfile(profile.id);
                                }}
                              >
                                Start Conversation
                              </button>
                            ) : (
                              <button
                                className="button-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditProfile(profile, e);
                                }}
                              >
                                Complete Setup
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p style={{ marginTop: '20px', fontSize: '16px', color: 'var(--text-muted)' }}>
                    No profiles found. Create your first profile below.
                  </p>
                )}

                <button
                  className="button-primary"
                  onClick={() => setStep(1)}
                  style={{ marginTop: '30px' }}
                >
                  Create New Profile
                </button>
          </div>
        )}
        
        {step === 1 && (
          <div className="step-content">
            <h2>Step 1: Profile Information</h2>
            
            <div className="input-group">
              <label>Elderly User ID</label>
              <input
                type="text"
                value={elderlyUserId}
                onChange={(e) => handleElderlyUserIdChange(e.target.value)}
                placeholder="Enter elderly user ID (e.g., grandma, mom, dad)"
              />
            </div>
            
            <div className="input-group">
              <label>Family Member User ID (Your Name)</label>
              <input
                type="text"
                value={familyUserId}
                onChange={(e) => setFamilyUserId(e.target.value)}
                placeholder="Enter your name or ID"
              />
            </div>
            
            <div className="input-group">
              <label>Relationship</label>
              <select
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                style={{ width: '100%', padding: '16px', fontSize: '18px', borderRadius: '8px' }}
              >
                <option value="">-- Select Relationship --</option>
                <option value="son">Son</option>
                <option value="daughter">Daughter</option>
                <option value="grandson">Grandson</option>
                <option value="granddaughter">Granddaughter</option>
                <option value="nephew">Nephew</option>
                <option value="niece">Niece</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="input-group">
              <label>Language Preference</label>
              <select
                value={languagePref}
                onChange={(e) => setLanguagePref(e.target.value)}
                style={{ width: '100%', padding: '16px', fontSize: '18px', borderRadius: '8px' }}
              >
                <option value="auto">Auto-detect</option>
                <option value="ja">Japanese</option>
                <option value="en">English</option>
              </select>
            </div>

            {/* Optional manual persona text */}
            <div className="input-group" style={{ marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => setShowPersonaInput(!showPersonaInput)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--sage-600)',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500'
                }}
              >
                {showPersonaInput ? '▼' : '▶'} Add Manual Description (Optional)
              </button>
              
              {showPersonaInput && (
                <div style={{ marginTop: '10px' }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                    You can add a brief description here. The personalization interview will generate a more detailed profile.
                  </p>
                  <textarea
                    value={personaText}
                    onChange={(e) => setPersonaText(e.target.value)}
                    placeholder="Optional: Describe how this family member speaks, their personality, topics they discuss, etc."
                    style={{ height: '100px' }}
                  />
                </div>
              )}
            </div>

            <div style={{ 
              background: 'var(--sage-50)', 
              padding: '20px', 
              borderRadius: '12px', 
              marginTop: '30px',
              marginBottom: '20px',
              border: '1px solid var(--sage-200)'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: 'var(--sage-700)' }}>📋 Next Steps</h3>
              <p style={{ margin: 0, color: 'var(--text-primary)' }}>
                After creating this profile, you'll go through a <strong>voice interview</strong> to help us understand 
                your personality and create a personalized AI companion for your loved one. Then you'll upload a voice sample.
              </p>
            </div>
            
            <button
              className="button-primary"
              onClick={async () => {
                await handleCreateProfile();
              }}
              disabled={loading || !elderlyUserId || !familyUserId || !relationship}
            >
              {loading ? 'Creating...' : 'Create Profile & Start Interview'}
            </button>
            
            <button
              className="button-secondary"
              onClick={() => setStep(0)}
              style={{ marginLeft: '15px' }}
            >
              Cancel
            </button>
          </div>
        )}
        
        {step === 2 && (
          <div className="step-content">
            <h2>{editingProfile ? 'Upload/Record Voice Sample' : 'Step 2: Upload Voice Sample'}</h2>
            <p style={{ fontSize: '18px', marginBottom: '20px' }}>
              Please provide a voice sample (10-30 seconds of natural speech)
            </p>
            
            {/* Method selection */}
            <div className="input-group" style={{ marginBottom: '20px' }}>
              <label>Choose Method</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setInputMethod('upload');
                    setVoiceFile(null);
                    setRecordedAudio(null);
                    setRecordingTime(0);
                  }}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    borderRadius: '10px',
                    border: '2px solid',
                    borderColor: inputMethod === 'upload' ? 'var(--sage-500)' : 'var(--border)',
                    backgroundColor: inputMethod === 'upload' ? 'var(--sage-100)' : 'white',
                    cursor: 'pointer',
                    fontWeight: inputMethod === 'upload' ? '600' : 'normal',
                    color: inputMethod === 'upload' ? 'var(--sage-700)' : 'var(--text-primary)'
                  }}
                >
                  📁 Upload File
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setInputMethod('record');
                    setVoiceFile(null);
                    setRecordedAudio(null);
                    setRecordingTime(0);
                  }}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    borderRadius: '10px',
                    border: '2px solid',
                    borderColor: inputMethod === 'record' ? 'var(--sage-500)' : 'var(--border)',
                    backgroundColor: inputMethod === 'record' ? 'var(--sage-100)' : 'white',
                    cursor: 'pointer',
                    fontWeight: inputMethod === 'record' ? '600' : 'normal',
                    color: inputMethod === 'record' ? 'var(--sage-700)' : 'var(--text-primary)'
                  }}
                >
                  🎤 Record Voice
                </button>
              </div>
            </div>
            
            {/* Upload method */}
            {inputMethod === 'upload' && (
              <div className="input-group">
                <label>Voice Sample File</label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => {
                    setVoiceFile(e.target.files[0]);
                    setRecordedAudio(null);
                  }}
                  style={{ fontSize: '18px', padding: '10px' }}
                />
                {voiceFile && (
                  <p style={{ marginTop: '10px', color: 'var(--sage-600)', fontSize: '14px', fontWeight: '500' }}>
                    ✓ File selected: {voiceFile.name} ({(voiceFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            )}
            
            {/* Record method */}
            {inputMethod === 'record' && (
              <div className="input-group">
                <label>Record Voice Sample</label>
                <div style={{ 
                  border: '2px dashed var(--sage-300)', 
                  borderRadius: '12px', 
                  padding: '30px',
                  textAlign: 'center',
                  backgroundColor: isRecording ? 'var(--mint-50)' : 'var(--sage-50)'
                }}>
                  {!isRecording && !recordedAudio && (
                    <div>
                      <p style={{ fontSize: '16px', marginBottom: '20px', color: 'var(--text-muted)' }}>
                        Click the button below to start recording
                      </p>
                      <button
                        onClick={startRecording}
                        className="button-primary"
                        style={{
                          padding: '15px 30px',
                          fontSize: '18px'
                        }}
                      >
                        🎤 Start Recording
                      </button>
                    </div>
                  )}
                  
                  {isRecording && (
                    <div>
                      <div style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--mint-600)' }}>
                        🔴 Recording...
                      </div>
                      <div style={{ fontSize: '32px', marginBottom: '20px', fontWeight: 'bold', color: 'var(--sage-700)' }}>
                        {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                      </div>
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                        Speak naturally for 10-30 seconds
                      </p>
                      <button
                        onClick={stopRecording}
                        style={{
                          padding: '15px 30px',
                          fontSize: '18px',
                          backgroundColor: 'var(--forest-700)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        ⏹ Stop Recording
                      </button>
                    </div>
                  )}
                  
                  {recordedAudio && !isRecording && (
                    <div>
                      <p style={{ fontSize: '16px', marginBottom: '20px', color: 'var(--sage-600)', fontWeight: '600' }}>
                        ✓ Recording complete!
                      </p>
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                        Duration: {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')} 
                        ({(recordedAudio.size / 1024).toFixed(2)} KB)
                      </p>
                      <audio 
                        controls 
                        src={URL.createObjectURL(recordedAudio)}
                        style={{ width: '100%', marginBottom: '20px' }}
                      />
                      <button
                        onClick={() => {
                          setRecordedAudio(null);
                          setVoiceFile(null);
                          setRecordingTime(0);
                        }}
                        className="button-secondary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '16px',
                          marginRight: '10px'
                        }}
                      >
                        🔄 Record Again
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '30px' }}>
              <button
                className="button-primary"
                onClick={handleUploadVoice}
                disabled={loading || (inputMethod === 'upload' && !voiceFile) || (inputMethod === 'record' && !recordedAudio) || isRecording}
              >
                {loading ? 'Uploading...' : 'Upload Voice'}
              </button>
              
              <button
                className="button-secondary"
                onClick={() => {
                  setStep(0);
                  // Cleanup recording if active
                  if (isRecording) {
                    stopRecording();
                  }
                  if (mediaStream) {
                    mediaStream.getTracks().forEach(track => track.stop());
                    setMediaStream(null);
                  }
                }}
                style={{ marginLeft: '20px' }}
              >
                Back
              </button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="step-content">
            <h2>Profile Created Successfully!</h2>
            <p style={{ fontSize: '20px', marginBottom: '30px' }}>
              Your family voice profile has been created and the voice has been processed.
            </p>
            
            <button
              className="button-primary"
              onClick={() => window.location.href = `/conversation-realtime/${profileId}`}
            >
              Start Conversation
            </button>
            
            <button
              className="button-secondary"
              onClick={() => {
                setStep(0);
                setProfileId(null);
                setVoiceFile(null);
                setSuccess(null);
                loadExistingProfiles();
              }}
              style={{ marginLeft: '20px' }}
            >
              Back to Profiles
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="step-content">
            <h2>Edit Profile Details</h2>
            <p style={{ fontSize: '16px', marginBottom: '20px', color: 'var(--text-muted)' }}>
              Update the profile information below
            </p>

            {/* Interview Status Banner */}
            <div style={{
              background: editingProfile?.survey_completed ? 'var(--mint-50)' : 'var(--sage-50)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              border: `2px solid ${editingProfile?.survey_completed ? 'var(--mint-300)' : 'var(--sage-300)'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                  <h3 style={{ margin: '0 0 5px 0', color: editingProfile?.survey_completed ? 'var(--mint-700)' : 'var(--sage-700)' }}>
                    {editingProfile?.survey_completed ? '✅ Personalization Interview Complete' : '📝 Personalization Interview'}
                  </h3>
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                    {editingProfile?.survey_completed 
                      ? 'Your profile was generated from the interview. You can redo it to update.'
                      : 'Complete a voice interview to automatically generate a rich personality profile.'}
                  </p>
                </div>
                <button
                  onClick={() => handleStartSurvey(profileId)}
                  className="button-primary"
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {editingProfile?.survey_completed ? '🔄 Redo Interview' : '🎤 Start Interview'}
                </button>
                <button
                  onClick={() => handleStartEnrichment(profileId)}
                  className="button-secondary"
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ✨ Import Memories
                </button>
              </div>
            </div>
            
            <div className="input-group">
              <label>Relationship</label>
              <input
                type="text"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                placeholder="e.g., son, daughter, grandchild"
              />
            </div>
            
            <div className="input-group">
              <label>Persona Description {editingProfile?.survey_completed && <span style={{color: 'var(--sage-600)', fontSize: '12px'}}>(auto-generated from interview)</span>}</label>
              <textarea
                value={personaText}
                onChange={(e) => setPersonaText(e.target.value)}
                placeholder="Describe how this family member speaks, their personality, topics they discuss, etc."
                style={{ minHeight: '150px' }}
              />
            </div>
            
            <div className="input-group">
              <label>Language Preference</label>
              <select
                value={languagePref}
                onChange={(e) => setLanguagePref(e.target.value)}
                style={{ width: '100%', padding: '16px', fontSize: '20px', borderRadius: '8px' }}
              >
                <option value="auto">Auto-detect</option>
                <option value="ja">Japanese</option>
                <option value="en">English</option>
              </select>
            </div>

            {editingProfile?.setup_complete && (
              <div className="input-group" style={{
                marginTop: '24px',
                padding: '20px',
                background: 'var(--sage-50)',
                borderRadius: '12px',
                border: '2px solid var(--sage-200)',
              }}>
                <label>Link Elderly User Account</label>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '8px 0 16px' }}>
                  After setup is complete, link the elderly person&apos;s login email so they can use the simple /home interface.
                </p>
                {linkedElderly ? (
                  <p style={{ marginBottom: '12px' }}>
                    ✅ Linked: <strong>{linkedElderly.username}</strong> ({linkedElderly.email})
                  </p>
                ) : (
                  <p style={{ marginBottom: '12px', color: 'var(--text-muted)' }}>
                    No elderly account linked yet.
                  </p>
                )}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="email"
                    value={elderlyLinkEmail}
                    onChange={(e) => setElderlyLinkEmail(e.target.value)}
                    placeholder="elderly.person@email.com"
                    style={{ flex: 1, minWidth: '220px', padding: '12px', fontSize: '16px' }}
                  />
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={handleLinkElderly}
                    disabled={loading}
                  >
                    Link Account
                  </button>
                </div>
                {linkMessage && (
                  <p style={{
                    marginTop: '12px',
                    color: linkMessage.type === 'error' ? 'var(--error)' : 'var(--mint-700)',
                  }}>
                    {linkMessage.text}
                  </p>
                )}
              </div>
            )}
            
            <div style={{ marginTop: '30px' }}>
              <button
                className="button-primary"
                onClick={handleUpdateProfile}
                disabled={loading || !relationship || !personaText}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
              
              <button
                className="button-secondary"
                onClick={() => {
                  // Go to voice upload/recording step to upload/re-record voice
                  setStep(2);
                  setVoiceFile(null);
                  setRecordedAudio(null);
                  setRecordingTime(0);
                }}
                style={{ marginLeft: '10px' }}
              >
                {editingProfile?.voice_ready ? 'Re-record Voice' : 'Upload/Record Voice'}
              </button>
              
              <button
                className="button-secondary"
                onClick={() => {
                  setStep(0);
                  setEditingProfile(null);
                  setProfileId(null);
                  loadExistingProfiles();
                }}
                style={{ marginLeft: '10px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonalizationPage;



